import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2,
  Calendar,
  Users,
  MessageSquare,
  Megaphone,
  Plus,
  Check,
  X,
  Clock,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { gradeSubmissionFn, createAssignmentFn } from "@/lib/assignments.functions";
import { markAttendanceFn } from "@/lib/attendance.functions";

export const Route = createFileRoute("/_authenticated/instructor/group/$groupId")({
  component: GroupDetail,
});

interface Student {
  student_id: string;
  profile: { full_name: string | null; points: number } | null;
}
type Status = "present" | "absent" | "late" | "excused";

function GroupDetail() {
  const { groupId } = Route.useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState<{ name: string; courses: { name: string } | null } | null>(
    null,
  );
  const [students, setStudents] = useState<Student[]>([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState<Record<string, Status>>({});
  const [announcements, setAnnouncements] = useState<
    { id: string; title: string; content: string; created_at: string }[]
  >([]);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");

  const [assignments, setAssignments] = useState<any[]>([]);
  const [activeAssignment, setActiveAssignment] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [gradingSub, setGradingSub] = useState<any | null>(null);
  const [gradeVal, setGradeVal] = useState("");
  const [feedbackVal, setFeedbackVal] = useState("");

  const load = async () => {
    const [g, gm, an, asm] = await Promise.all([
      supabase.from("groups").select("name,courses(name)").eq("id", groupId).maybeSingle(),
      supabase.from("group_members").select("student_id").eq("group_id", groupId),
      supabase
        .from("announcements")
        .select("id,title,content,created_at")
        .eq("group_id", groupId)
        .order("created_at", { ascending: false }),
      supabase
        .from("assignments")
        .select("*")
        .eq("group_id", groupId)
        .order("created_at", { ascending: false }),
    ]);
    setGroup(g.data as unknown as typeof group);
    const ids = (gm.data ?? []).map((r) => r.student_id as string);
    if (ids.length) {
      const pr = await supabase.from("profiles").select("id,full_name,points").in("id", ids);
      setStudents(
        (pr.data ?? []).map((p) => ({
          student_id: p.id as string,
          profile: { full_name: p.full_name as string | null, points: (p.points as number) ?? 0 },
        })),
      );
    } else setStudents([]);
    setAnnouncements((an.data ?? []) as typeof announcements);
    setAssignments(asm.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [groupId]);

  useEffect(() => {
    if (!students.length) return;
    supabase
      .from("attendance")
      .select("student_id,status")
      .eq("group_id", groupId)
      .eq("session_date", date)
      .then(({ data }) => {
        const m: Record<string, Status> = {};
        (data ?? []).forEach((r) => {
          m[r.student_id as string] = r.status as Status;
        });
        setAttendance(m);
      });
  }, [date, groupId, students.length]);

  const mark = async (studentId: string, status: Status) => {
    setAttendance((a) => ({ ...a, [studentId]: status }));
    try {
      await markAttendanceFn({
        data: {
          group_id: groupId,
          student_id: studentId,
          session_date: date,
          status,
        },
      });
    } catch (err: any) {
      toast.error(err.message || "حدث خطأ أثناء تسجيل الحضور");
    }
  };

  const postAnnouncement = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const { error } = await supabase.from("announcements").insert({
      group_id: groupId,
      author_id: user!.id,
      title: String(fd.get("title")),
      content: String(fd.get("content")),
    });
    if (error) return toast.error(error.message);
    toast.success("تم النشر");
    (e.currentTarget as HTMLFormElement).reset();
    load();
  };

  const saveNote = async (studentId: string) => {
    if (!noteText.trim()) return;
    const { error } = await supabase.from("instructor_notes").insert({
      student_id: studentId,
      instructor_id: user!.id,
      content: noteText,
      is_private: false,
    });
    if (error) return toast.error(error.message);
    toast.success("تم حفظ الملاحظة");
    setNoteFor(null);
    setNoteText("");
  };

  const createAssignment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await createAssignmentFn({
        data: {
          group_id: groupId,
          title: String(fd.get("title")),
          description: String(fd.get("description")),
          deadline: String(fd.get("deadline")) || null,
          max_grade: Number(fd.get("max_grade") || 100),
        },
      });
      toast.success("تم إنشاء الواجب");
      (e.currentTarget as HTMLFormElement).reset();
      load();
    } catch (err: any) {
      toast.error(err.message || "حدث خطأ");
    }
  };

  const loadSubmissions = async (asmId: string) => {
    setActiveAssignment(activeAssignment === asmId ? null : asmId);
    if (activeAssignment === asmId) return;
    const { data } = await supabase
      .from("assignment_submissions")
      .select("*, profiles:student_id(full_name)")
      .eq("assignment_id", asmId);
    setSubmissions(data ?? []);
  };

  const submitGrade = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!gradingSub) return;
    try {
      await gradeSubmissionFn({
        data: {
          submissionId: gradingSub.id,
          grade: Number(gradeVal),
          feedback: feedbackVal,
        },
      });
      toast.success("تم التقييم");
      setGradingSub(null);
      loadSubmissions(gradingSub.assignment_id);
    } catch (err: any) {
      toast.error(err.message || "حدث خطأ");
    }
  };

  if (loading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="container mx-auto px-4 py-10">
      <Link to="/instructor" className="text-sm text-muted-foreground hover:text-primary">
        ← كل الجروبات
      </Link>
      <h1 className="text-3xl font-extrabold mt-2 mb-1">{group?.name}</h1>
      <p className="text-muted-foreground mb-8">{group?.courses?.name}</p>

      <Tabs defaultValue="attendance">
        <TabsList className="rounded-full">
          <TabsTrigger value="attendance" className="rounded-full">
            <Calendar className="h-4 w-4 me-2" />
            الحضور
          </TabsTrigger>
          <TabsTrigger value="assignments" className="rounded-full">
            <FileText className="h-4 w-4 me-2" />
            الواجبات
          </TabsTrigger>
          <TabsTrigger value="students" className="rounded-full">
            <Users className="h-4 w-4 me-2" />
            الطلاب
          </TabsTrigger>
          <TabsTrigger value="announcements" className="rounded-full">
            <Megaphone className="h-4 w-4 me-2" />
            الإعلانات
          </TabsTrigger>
        </TabsList>

        <TabsContent value="attendance" className="mt-6">
          <div className="flex items-center gap-3 mb-4">
            <Label htmlFor="date">التاريخ:</Label>
            <Input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-auto"
            />
          </div>
          <div className="space-y-2">
            {students.map((s) => {
              const st = attendance[s.student_id];
              return (
                <Card key={s.student_id} className="rounded-2xl border-0 shadow-soft">
                  <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-bold flex-1 min-w-0">
                      {s.profile?.full_name || "Student"}
                    </div>
                    <div className="flex gap-1">
                      {(["present", "late", "absent", "excused"] as Status[]).map((opt) => (
                        <Button
                          key={opt}
                          size="sm"
                          variant={st === opt ? "default" : "outline"}
                          onClick={() => mark(s.student_id, opt)}
                          className={`rounded-full ${st === opt && opt === "present" ? "bg-emerald-500 hover:bg-emerald-600" : ""} ${st === opt && opt === "absent" ? "bg-rose-500 hover:bg-rose-600" : ""} ${st === opt && opt === "late" ? "bg-amber-500 hover:bg-amber-600" : ""}`}
                        >
                          {opt === "present" && <Check className="h-3 w-3" />}
                          {opt === "absent" && <X className="h-3 w-3" />}
                          {opt === "late" && <Clock className="h-3 w-3" />}
                          <span className="ms-1 text-xs">
                            {opt === "present"
                              ? "حاضر"
                              : opt === "absent"
                                ? "غايب"
                                : opt === "late"
                                  ? "متأخر"
                                  : "بعذر"}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {!students.length && (
              <p className="text-muted-foreground text-center py-8">لا يوجد طلاب في هذا الجروب</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="students" className="mt-6">
          <div className="space-y-3">
            {students.map((s) => (
              <Card key={s.student_id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold">{s.profile?.full_name}</div>
                      <Badge variant="secondary" className="mt-1">
                        {s.profile?.points ?? 0} pts
                      </Badge>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => {
                        setNoteFor(noteFor === s.student_id ? null : s.student_id);
                        setNoteText("");
                      }}
                    >
                      <MessageSquare className="h-4 w-4 me-1" /> ملاحظة لولي الأمر
                    </Button>
                  </div>
                  {noteFor === s.student_id && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="اكتب ملاحظتك..."
                        rows={3}
                      />
                      <Button
                        size="sm"
                        onClick={() => saveNote(s.student_id)}
                        className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                      >
                        حفظ
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <Card className="rounded-3xl border-0 shadow-soft mb-6">
            <CardContent className="p-6">
              <form onSubmit={postAnnouncement} className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="title">عنوان</Label>
                  <Input id="title" name="title" required />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="content">المحتوى</Label>
                  <Textarea id="content" name="content" required rows={3} />
                </div>
                <Button
                  type="submit"
                  className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                >
                  <Plus className="h-4 w-4 me-2" />
                  نشر
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-3">
            {announcements.map((a) => (
              <Card key={a.id} className="rounded-2xl border-0 shadow-soft">
                <CardContent className="p-4">
                  <div className="font-bold">{a.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{a.content}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(a.created_at).toLocaleString("ar-EG")}
                  </p>
                </CardContent>
              </Card>
            ))}
            {!announcements.length && (
              <p className="text-muted-foreground text-center py-8">لا توجد إعلانات</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="mt-6">
          <Card className="rounded-3xl border-0 shadow-soft mb-6">
            <CardContent className="p-6">
              <form onSubmit={createAssignment} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="asm_title">عنوان الواجب</Label>
                    <Input id="asm_title" name="title" required />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="asm_deadline">الموعد النهائي</Label>
                    <Input id="asm_deadline" name="deadline" type="datetime-local" required />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="asm_desc">الوصف</Label>
                  <Textarea id="asm_desc" name="description" rows={3} required />
                </div>
                <Button
                  type="submit"
                  className="rounded-full bg-gradient-primary text-primary-foreground border-0"
                >
                  <Plus className="h-4 w-4 me-2" />
                  إنشاء واجب
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {assignments.map((asm) => (
              <Card key={asm.id} className="rounded-2xl border-0 shadow-soft overflow-hidden">
                <CardContent className="p-0">
                  <div
                    className="p-4 cursor-pointer hover:bg-muted/30 flex justify-between items-center"
                    onClick={() => loadSubmissions(asm.id)}
                  >
                    <div>
                      <div className="font-bold">{asm.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3" />{" "}
                        {new Date(asm.deadline).toLocaleString("ar-EG")}
                      </div>
                    </div>
                    <Badge variant="outline">{asm.max_grade} درجة</Badge>
                  </div>

                  {activeAssignment === asm.id && (
                    <div className="border-t bg-muted/10 p-4 space-y-3">
                      <div className="text-sm text-muted-foreground mb-4">{asm.description}</div>

                      <h4 className="font-bold text-sm">التسليمات:</h4>
                      {submissions.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                          لم يقم أحد بتسليم الواجب حتى الآن.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {submissions.map((sub) => (
                            <div
                              key={sub.id}
                              className="p-3 bg-background rounded-xl border flex flex-col gap-2"
                            >
                              <div className="flex justify-between items-center">
                                <span className="font-semibold text-sm">
                                  {sub.profiles?.full_name}
                                </span>
                                {sub.status === "graded" ? (
                                  <Badge className="bg-emerald-500">
                                    {sub.grade}/{asm.max_grade}
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">جاري التقييم</Badge>
                                )}
                              </div>
                              {sub.submission_url && (
                                <a
                                  href={sub.submission_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-xs text-primary hover:underline break-all"
                                >
                                  {sub.submission_url}
                                </a>
                              )}
                              {sub.submission_note && (
                                <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                                  {sub.submission_note}
                                </p>
                              )}

                              {gradingSub?.id === sub.id ? (
                                <form
                                  onSubmit={submitGrade}
                                  className="mt-2 space-y-2 p-3 bg-muted/50 rounded-xl border border-primary/20"
                                >
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      max={asm.max_grade}
                                      min={0}
                                      value={gradeVal}
                                      onChange={(e) => setGradeVal(e.target.value)}
                                      placeholder="الدرجة"
                                      className="w-24"
                                      required
                                    />
                                    <span className="text-sm text-muted-foreground">
                                      / {asm.max_grade}
                                    </span>
                                  </div>
                                  <Textarea
                                    value={feedbackVal}
                                    onChange={(e) => setFeedbackVal(e.target.value)}
                                    placeholder="ملاحظات للتقييم..."
                                    rows={2}
                                    required
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      type="submit"
                                      size="sm"
                                      className="rounded-full bg-gradient-primary"
                                    >
                                      حفظ التقييم
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setGradingSub(null)}
                                    >
                                      إلغاء
                                    </Button>
                                  </div>
                                </form>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-1 w-max"
                                  onClick={() => {
                                    setGradingSub(sub);
                                    setGradeVal(sub.grade?.toString() || "");
                                    setFeedbackVal(sub.feedback || "");
                                  }}
                                >
                                  {sub.status === "graded" ? "تعديل التقييم" : "تقييم الواجب"}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
